import { multiplier } from "@local/extensions/utilities/scales.js";

// Build float noise only: the analyzer wrote drawing_offset with the same
// arithmetic predictOffset uses, so an untouched frame matches almost
// exactly. Anything a human moved misses by far more than this.
const UNTOUCHED_EPSILON = 1e-6;

/**
 * Where the registration point pins the frame origin under scale `m`, in
 * real-world units — the same 300dpi math `analyze-opening-clips.js` uses
 * at build time. The stored point is the frame's measured TOP-left in clip
 * pixels (y from the image top); the frame height converts it to the
 * bottom-left the offset encodes.
 */
function predictOffset(clip, heightInches, m) {
  const ch = (heightInches * 300) / m;
  const ypx = clip.height - ch - clip.registration_point.y;
  const xpx = clip.registration_point.x;
  return { x: (-xpx * m) / 300, y: (-ypx * m) / 300 };
}

/**
 * Return a copy of `clip` carrying `newScale`, with `drawing_offset` moved
 * so the drawing resizes about the frame's origin instead of the crop-box
 * corner. Two regimes:
 *
 * - **Untouched since build** (the stored offset is exactly what
 *   `clip.registration_point` predicts under the old scale, and the caller
 *   supplied `opts.heightInches`): re-derive the offset from the point under
 *   the new scale. The measured top-left pixel snaps back onto the drawing
 *   and the bottom is recomputed — the "built with the wrong scale, then
 *   corrected it" workflow.
 * - **Anything else** — the user dragged the overlay or edited the frame's
 *   size (prediction mismatch), the point was never stored, or no height is
 *   available: rescale the offset proportionally by the multiplier ratio,
 *   which keeps whatever origin pixel the pair currently encodes. A human's
 *   position always wins over the detected one.
 *
 * When either scale is missing or unparsable (`multiplier` throws on bad
 * custom strings) there is no basis for conversion, so the offset passes
 * through unchanged — the pre-existing behavior of every call site.
 */
export function withClipScale(clip, newScale, opts = {}) {
  const out = { ...clip, drawing_scale: newScale };
  if (!clip.drawing_offset || !clip.drawing_scale || !newScale) return out;

  let mOld, mNew;
  try {
    mOld = multiplier(clip.drawing_scale);
    mNew = multiplier(newScale);
  } catch {
    return out;
  }

  if (clip.registration_point && opts.heightInches) {
    const predicted = predictOffset(clip, opts.heightInches, mOld);
    if (
      Math.abs(predicted.x - clip.drawing_offset.x) <= UNTOUCHED_EPSILON &&
      Math.abs(predicted.y - clip.drawing_offset.y) <= UNTOUCHED_EPSILON
    ) {
      out.drawing_offset = predictOffset(clip, opts.heightInches, mNew);
      return out;
    }
  }

  const ratio = mNew / mOld;
  out.drawing_offset = {
    x: clip.drawing_offset.x * ratio,
    y: clip.drawing_offset.y * ratio,
  };
  return out;
}
